import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as yup from "yup";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BackArrow,
  Button,
  CheckBox,
  Input,
  Select,
  TopProgress,
} from "../../../../components";
import { clientService } from "../../../../api";
import { useUi } from "../../../../context/ui.context";
import { usePageTitle } from "../../../../context/navigationContext";
import useDebounce from "../../../../hooks/useDebounce";
import {
  Container,
  CreateContainer,
  FormWrapper,
  TitleBig,
} from "../../client-groups/create/styles";

type SelectOption = {
  id: string | number;
  value: string | number;
  label: string;
};

const mapClientOption = (client: any): SelectOption => ({
  id: client.id,
  value: client.id,
  label: client.name || client.display || `#${client.id}`,
});

const mapRoleOption = (role: any): SelectOption => ({
  id: role.id,
  value: role.id,
  label: role.display_name || role.name || `#${role.id}`,
});

const CreateRole: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roleId } = useParams();
  const [t] = useTranslation();
  const { viewAlert } = useUi();

  const isEdit = useMemo(
    () => !location.pathname.includes("/create"),
    [location.pathname]
  );

  usePageTitle(isEdit ? `${t("EDIT")} ${t("PAGE_TITLE_ROLE_DETAIL")}` : t("CREATE_ROLE"));

  const [role, setRole] = useState<any>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(isEdit);
  const [saving, setSaving] = useState<boolean>(false);
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false);
  const [ownerOptions, setOwnerOptions] = useState<SelectOption[]>([]);
  const [parentOptions, setParentOptions] = useState<SelectOption[]>([]);
  const [ownerSearch, setOwnerSearch] = useState<string>("");
  const [parentSearch, setParentSearch] = useState<string>("");

  const debouncedOwnerSearch = useDebounce(ownerSearch, 300);
  const debouncedParentSearch = useDebounce(parentSearch, 300);
  const isLocked = isEdit && Boolean(role?.default);

  const loadRole = useCallback(async () => {
    if (!roleId || !isEdit) {
      setPageLoading(false);
      return;
    }

    setPageLoading(true);
    try {
      const response = await clientService.getUserRole(roleId);
      setRole(response?.data);
    } catch (e: any) {
      viewAlert({
        severity: "error",
        message:
          e?.response?.data?.detail ||
          e?.response?.data?.error ||
          t("FAILED_TO_LOAD_DATA"),
      });
    } finally {
      setPageLoading(false);
    }
  }, [isEdit, roleId, t, viewAlert]);

  useEffect(() => {
    loadRole();
  }, [loadRole]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: role?.name || "",
      owner: role?.owner
        ? mapClientOption({
            id: role.owner,
            name: role.owner_display,
          })
        : null,
      parent: role?.parent
        ? mapRoleOption({
            id: role.parent,
            name: role.parent_display,
            display_name: role.parent_display,
          })
        : null,
      public: Boolean(role?.public),
    },
    validationSchema: yup.object({
      name: yup.string().trim().required(String(t("FIELD_REQUIRED"))),
    }),
    onSubmit: async (values) => {
      const payload = {
        name: values.name.trim(),
        owner: values.owner?.id || null,
        parent: values.parent?.id || null,
        public: values.public,
      };

      setSaving(true);
      try {
        if (isEdit && role) {
          await clientService.editUserRole({
            ...role,
            ...payload,
            id: role.id,
          });
        } else {
          await clientService.createUserRole(payload);
        }

        setLoadingComplete(true);
        viewAlert({
          severity: "success",
          message: t("ALL_CHANGES_SAVED_SUCCESSFULLY"),
        });
        setTimeout(() => {
          navigate("/clients-users/roles");
        }, 500);
      } catch (e: any) {
        viewAlert({
          severity: "error",
          message:
            e?.response?.data?.detail ||
            e?.response?.data?.error ||
            t("ERROR_OCCURRED"),
        });
      } finally {
        setSaving(false);
      }
    },
  });

  const loadOwners = useCallback(
    async (searchValue: string, fallback?: SelectOption | null) => {
      try {
        const response = await clientService.getClientList({
          page: 1,
          pageSize: 20,
          ordering: "name",
          search: searchValue || "",
          filtering: "",
        });

        const mapped = (response?.data?.objects || []).map(mapClientOption);
        if (fallback && !mapped.some((item: SelectOption) => item.id === fallback.id)) {
          mapped.push(fallback);
        }
        setOwnerOptions(mapped);
      } catch (error) {
        console.error("Failed to load role owners", error);
      }
    },
    []
  );

  const loadParents = useCallback(
    async (searchValue: string, fallback?: SelectOption | null) => {
      try {
        const response = await clientService.getRolePossibleParents(
          roleId
            ? { search: searchValue || "", current_role: roleId }
            : { search: searchValue || "" }
        );

        const mapped = (response?.data?.objects || []).map(mapRoleOption);
        if (fallback && !mapped.some((item: SelectOption) => item.id === fallback.id)) {
          mapped.push(fallback);
        }
        setParentOptions(mapped);
      } catch (error) {
        console.error("Failed to load role parents", error);
      }
    },
    [roleId]
  );

  useEffect(() => {
    loadOwners(debouncedOwnerSearch, formik.values.owner);
  }, [debouncedOwnerSearch, formik.values.owner, loadOwners]);

  useEffect(() => {
    loadParents(debouncedParentSearch, formik.values.parent);
  }, [debouncedParentSearch, formik.values.parent, loadParents]);

  return (
    <CreateContainer>
      <Container>
        <TopProgress loading={pageLoading || saving} />
        <BackArrow fallbackPath="/clients-users/roles" />
        <TitleBig style={{ margin: "0px" }}>
          {isEdit
            ? `${t("EDIT")} ${role?.display_name || role?.name || t("PAGE_TITLE_ROLE_DETAIL")}`
            : t("CREATE_ROLE")}
        </TitleBig>

        <FormWrapper>
          <Input
            fieldName="name"
            title={t("NAME")}
            placeholder=""
            required
            customStyles={{ width: "100%" }}
            disabled={isLocked}
            errors={formik.errors}
            touched={formik.touched}
            values={formik.values}
            handleChange={formik.handleChange}
            handleBlur={formik.handleBlur}
          />

          <Select
            data={ownerOptions}
            selectedValue={formik.values.owner?.id || ""}
            onChange={(option: SelectOption | null) =>
              formik.setFieldValue("owner", option || null)
            }
            hasSearch
            onSearchChange={setOwnerSearch}
            title={t("OWNER")}
            placeholder={t("OWNER")}
            disabled={isLocked}
          />

          <Select
            data={parentOptions}
            selectedValue={formik.values.parent?.id || ""}
            onChange={(option: SelectOption | null) =>
              formik.setFieldValue("parent", option || null)
            }
            hasSearch
            onSearchChange={setParentSearch}
            title={t("PARENT")}
            placeholder={t("PARENT")}
            disabled={isLocked}
          />
        </FormWrapper>

        <CheckBox
          value={formik.values.public}
          onChange={() => {
            if (isLocked) {
              return;
            }
            formik.setFieldValue("public", !formik.values.public);
          }}
          title={t("PUBLIC")}
          disabled={isLocked}
        />

        <div style={{ display: "flex", marginTop: "20px" }}>
          <Button
            variant="primary"
            size="display"
            loading={saving}
            loadingComplete={loadingComplete}
            customStyles={{ marginRight: "10px" }}
            onClick={() => formik.handleSubmit()}
            title={isEdit ? t("EDIT") : t("CREATE")}
            disabled={isLocked}
          />
          <Button
            variant="stroke"
            size="display"
            onClick={() => navigate("/clients-users/roles")}
            title={t("CANCEL")}
          />
        </div>
      </Container>
    </CreateContainer>
  );
};

export default CreateRole;
